import { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { generateScore, generateTrendGraph } from 'helper/formatHeatmapGraphData';
import { useBreakpointFlags } from 'hooks/useBreakpointFlags';
import { DashboardBox, InfoTooltip, NoData } from 'components/simple';

const styles = {
  pillarCircle: ({ palette }) => ({
    width: 20,
    height: 20,
    borderRadius: '100px',
    background: palette.common.black,
    flexShrink: 0,
    marginRight: 1,
  }),
  dashboardBoxContent: ({ breakpoints }, totalGroup) => ({
    height: 'auto',
    minHeight: 300,
    flexDirection: 'column',
    justifyContent: totalGroup ? 'center' : 'space-around',
    gap: 5,
    padding: 2,
    paddingRight: 0,
    [breakpoints.up('md')]: {
      height: 'auto',
      minHeight: 400,
      padding: 3,
    },
  }),
  dashboardBoxHeader: ({ breakpoints }) => ({
    height: 'auto',
    minHeight: 50,
    [breakpoints.up('md')]: {
      height: 'auto',
      minHeight: 75,
    },
  }),
  dashboardBoxHeaderContent: ({ breakpoints }) => ({
    [breakpoints.up('md')]: {
      maxWidth: 347,
    },
  }),
  headerCell: {
    verticalAlign: 'top',
  },
  table: { tableLayout: 'fixed' },
};

const StyledTableCell = styled(TableCell)(({ theme: { palette, breakpoints } }) => ({
  textAlign: 'center',
  paddingBlock: '5px',
  paddingInline: '5px',
  borderRight: `1px solid ${palette.colors.lightGrey}`,

  [breakpoints.up('md')]: {
    paddingInline: '10px',
  },
}));

const Heatmap = ({ isComparison = false, data = [], selectValue = null, onChangeSelect, selectOptions }) => {
  const { isLgUp: isLgWidth, getResponsiveValueForCurrent } = useBreakpointFlags();
  const subtitleLength = getResponsiveValueForCurrent({
    xs: 30,
    sm: 55,
    md: 20,
    lg: 25,
    xl: 25,
    xxl: 25,
  });

  const [selectedDataGroupQuestion, setActiveDataGroupQuestion] = useState(0);

  const activeDataGroupQuestion = data[selectedDataGroupQuestion] ? selectedDataGroupQuestion : 0;
  const heatmapData = data[activeDataGroupQuestion] ?? null;
  const totalGroup = data.length;

  const nextGroup = () => {
    if (totalGroup - 1 !== activeDataGroupQuestion) {
      setActiveDataGroupQuestion(activeDataGroupQuestion + 1);
    } else {
      setActiveDataGroupQuestion(0);
    }
  };

  const prevGroup = () => {
    if (activeDataGroupQuestion !== 0) {
      setActiveDataGroupQuestion(activeDataGroupQuestion - 1);
    } else {
      setActiveDataGroupQuestion(totalGroup - 1);
    }
  };

  const tableHeaders = useMemo(() => {
    if (!heatmapData || !heatmapData.headers?.length) {
      return [];
    }

    const firstColumnWidth = isLgWidth ? '25%' : 160;
    const columnWidth = isLgWidth ? 'auto' : 80;

    return heatmapData.headers.map((header, index) => ({
      ...header,
      width: index === 0 ? firstColumnWidth : columnWidth,
    }));
  }, [heatmapData, isLgWidth]);

  return (
    <DashboardBox
      withSubtitleTooltip
      title={isComparison ? 'Comparison Heatmap' : 'Heatmap'}
      subTitle={heatmapData?.groupQuestion}
      {...(totalGroup > 1 && { next: nextGroup })}
      prev={prevGroup}
      customContentStyles={(theme) => styles.dashboardBoxContent(theme, totalGroup)}
      customHeaderStyles={styles.dashboardBoxHeader}
      customHeaderContentStyles={styles.dashboardBoxHeaderContent}
      selectValue={selectValue}
      selectOptions={selectOptions}
      onChangeSelect={onChangeSelect}
      subTitleLength={subtitleLength}
    >
      {!heatmapData ? (
        <NoData />
      ) : (
        <>
          <TableContainer>
            <Table sx={styles.table}>
              <TableHead>
                <TableRow>
                  {tableHeaders.map(({ name, id, width, color, statement, typographyProps }, headerIndex) => (
                    <StyledTableCell key={id} width={width} sx={headerIndex === 0 ? null : styles.headerCell}>
                      <Stack
                        minHeight={80}
                        spacing={2}
                        alignItems={headerIndex === 0 ? 'flex-start' : 'center'}
                        justifyContent={headerIndex === 0 ? 'center' : 'flex=start'}
                      >
                        {color && statement && (
                          <Stack direction="row" spacing={1}>
                            <Box sx={[styles.pillarCircle, { background: color }]} />
                            <InfoTooltip titleText={statement} />
                          </Stack>
                        )}

                        <Typography variant="caption" textAlign="center" {...typographyProps}>
                          {name}
                        </Typography>
                      </Stack>
                    </StyledTableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {heatmapData.rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.cells.map((cell, cellIndex) => (
                      <StyledTableCell
                        key={cell.id}
                        sx={cell.styles}
                        {...(cellIndex === 0 && {
                          component: 'th',
                          scope: 'row',
                        })}
                      >
                        <Stack
                          height={56}
                          alignItems={cellIndex === 0 ? 'flex-start' : 'center'}
                          justifyContent="center"
                        >
                          {cell.isTextValue && (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Typography variant="caption" color="inherit" {...cell.typographyProps}>
                                {cell.value}
                              </Typography>
                              {cell.tooltipText && <InfoTooltip titleText={cell.tooltipText} />}
                            </Stack>
                          )}

                          {cell.isNumberValue && (
                            <Typography variant="body1" component="span" color="inherit">
                              {cell.value}
                            </Typography>
                          )}

                          {cell.isTrendValue && <>{generateTrendGraph(cell.value)}</>}

                          {cell.isDeviationValue && <>{generateScore(cell.value)}</>}
                        </Stack>
                      </StyledTableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography display="block" variant="caption">
            (All scores are out of 7)
          </Typography>
        </>
      )}
    </DashboardBox>
  );
};

Heatmap.propTypes = {
  isComparison: PropTypes.bool,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      groupQuestion: PropTypes.string.isRequired,
      headers: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          name: PropTypes.string.isRequired,
          statement: PropTypes.string,
          color: PropTypes.string,
          typographyProps: PropTypes.shape({}),
        }),
      ),
      rows: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          cells: PropTypes.arrayOf(
            PropTypes.shape({
              id: PropTypes.string.isRequired,
              value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
              isTextValue: PropTypes.bool,
              isNumberValue: PropTypes.bool,
              isTrendValue: PropTypes.bool,
              isDeviationValue: PropTypes.bool,
              tooltipText: PropTypes.string,
              typographyProps: PropTypes.shape({}),
            }),
          ),
        }),
      ),
    }),
  ),
  selectValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChangeSelect: PropTypes.func.isRequired,
  selectOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ).isRequired,
};
export default Heatmap;
